<template>
    <div class="custom-field-order-widget" :class="componentClasses">
        <div class="widget-header">
            <div class="header-text">
                <h3>Edit Field Order</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="isLoading" class="loading-screen">
                <loading-screen-svg />
            </div>
            <div class="fields-container">
                <custom-field-draggable
                    v-for="customField in customFields"
                    :key="customField.id"
                    :custom-field="customField"
                    @update:dragging="updateDragging"
                    @reorder="reorderHeaders"
                />
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="cancel-btn transparent-btn" size="normal" @click="closeWidget">Cancel</app-button>
            </div>
            <div class="action-container">
                <app-button class="next-btn" @click="saveField">Save</app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { ResponsiveMixin } from 'vue-responsive-components';
import { CustomFieldsServiceMixin } from '@/mixins/custom-fields-service-mixin';
import { responsiveHelpers } from '@/scripts/helpers';

const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const CustomFieldDraggable = () => import('@/components/custom-field/custom-field-draggable');

export default {
    name: 'custom-field-order-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'custom-field-draggable': CustomFieldDraggable,
    },

    mixins: [ResponsiveMixin, CustomFieldsServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            dragging: false,
            customFields: [],
        };
    },

    breakpoints: responsiveHelpers.getBreakpoints(),

    computed: {
        componentClasses() {
            return [responsiveHelpers.getBreakpointClasses(this), { dragging: this.dragging }];
        },

        widgetType() {
            return this.widget.options.type;
        },

        clientId() {
            return this.widget.options.clientId;
        },

        agencyAccountId() {
            return this.widget.options.agencyAccountId;
        },

        requestConfig() {
            if (this.agencyAccountId) {
               return { type: this.widgetType, agencyAccountId: this.agencyAccountId };
            } else {
                return { type: this.widgetType, clientId: this.clientId };
            }
        },

        serviceUrl() {
            if (this.agencyAccountId) {
                return `/ms/custom-fields/v1/custom${this.widgetType}Fields/order/list/account/${this.agencyAccountId}`
            } else {
                return `/ms/custom-fields/v1/custom${this.widgetType}Fields/order/${this.clientId}/list`;
            }
        },
    },

    created() {
        this.getData();
    },

    methods: {
        updateDragging(dragging) {
            this.dragging = dragging;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getData() {
            this.getCustomFields({}, this.requestConfig)
                .then(({ items }) => {
                    this.customFields = items;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your custom fields.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        updateCustomFieldsOrder() {
            return new Promise((resolve, reject) => {
                const updatedCustomFields = {
                    Items: this.customFields.map((item) => {
                        return {
                            Id: item.id,
                            Order: item.order,
                        };
                    }),
                };

                this.$api
                    .put(
                        this.serviceUrl,
                        updatedCustomFields
                    )
                    .then(resolve)
                    .catch(reject);
            });
        },

        saveField() {
            this.updateCustomFieldsOrder()
                .then(() => {
                    this.$helpers.showSuccessNotification('Custom fields order updated successfully.');
                    EventBus.$emit('custom-field:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred trying to update the custom fields order.', { error });
                });
        },

        reorderHeaders(fromKey, toKey) {
            var toIndex = this.customFields.findIndex((field) => field.name === toKey);
            var movingHeader = this.customFields.find((field) => field.name === fromKey);
            var reorderedCustomFields = this.customFields.filter((field) => field.name !== fromKey);
            reorderedCustomFields.splice(toIndex, 0, movingHeader);
            this.customFields = reorderedCustomFields.map((field, index) => {
                return {
                    id: field.id,
                    name: field.name,
                    description: field.description,
                    inputType: field.inputType,
                    inputValue: field.values,
                    order: index + 1,
                };
            });
        },
    },
};
</script>

<style>
.custom-field-order-widget .fields-container {
    display: flex;
    flex-direction: column;
    row-gap: 11px;
}

.custom-field-order-widget .app-button {
    width: 127px;
}

.custom-field-order-widget .transparent-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.custom-field-order-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.custom-field-order-widget .cancel-btn {
    background: transparent;
    color: #382153;
    border: 2px solid #cac6cb;
}

.custom-field-order-widget .action-container {
    display: flex;
    column-gap: 13px;
}

.custom-field-order-widget .ghost-container {
    position: absolute;
    top: -10000px;
    left: -10000px;
    height: 40px !important;
    width: 640px !important;
    transform: rotate(0deg);
    pointer-events: none;
}

.custom-field-order-widget .ghost-container .ghost {
    position: relative;
    height: 100%;
    width: 100%;
    transform: rotate(-5deg);
}

.custom-field-order-widget .ghost-container .ghost .ghost-background {
    height: 100%;
    width: 100%;
    border-radius: 6px;
    border: 3px solid #ffffff;
    background: rgba(194, 183, 196, 1);
    background: linear-gradient(180deg, rgba(194, 183, 196, 1) 0%, rgba(135, 125, 136, 1) 100%);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    opacity: 0.9;
}

.custom-field-order-widget .ghost-container .ghost .ghost-text {
    position: absolute;
    top: 10px;
    width: 100%;
    font-size: 14px;
    z-index: 5;
    color: #ffffff;
    text-align: center;
}
</style>
